import React, { useState, useContext, useMemo } from "react";
import styled, { ThemeProvider } from "styled-components/macro";
import { Form } from "../../../../../../layout/FormLayout";
import { useFormWrapper } from "../../../../../../util/util";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import Axios from "axios";
import type { StorefrontMetaData } from "../../../../../../types/types";
import { endpoints } from "../../../../../../endpoints";
import { Store } from "../../../../../../Store";
import { TwoColumnLayout } from "../../../../../../layout/portalPageLayout";
import {
  SoftHeader2,
  H3,
} from "../../../../../../components/Typography/Typography";
import {
  PrimaryButtonMedium,
  CancelButtonSmall,
} from "../../../../../../components/Buttons/Buttons";
import { makeNewTheme } from "../../../../../../theme/themeUtils";
import type {
  ThemeObjectSchema,
  DesignTokensSchema,
} from "../../../../../../theme/themeTypes";
import { useTranslation } from "react-i18next";
import { CardProduct } from "../../../../../../components/CardProduct/CardProduct";
import { ButtonTestPage } from "../../../../../TestPages/ButtonTestPage";
import {
  colorPrimitiveTokens,
  spacingTokens,
  radiusTokens,
  borderPrimitiveTokens,
  containerSettingsTokens,
  productDetailLayoutTokens,
  AllTokenKeys,
  fontSizeTokens,
} from "../../../../../../designTokens";
import type { PIMProductMinimal } from "../../../../../../types/types.PIM";
import { ProductListMainWrapper } from "../../../../../../layout/publicPageLayout";
import { Auth } from "../../../../../../components/Auth";

// Create multiple mock products to showcase different card variations
const createMockProduct = (
  overrides: Partial<PIMProductMinimal>
): PIMProductMinimal => ({
  id: "demo-product",
  name: "Chemical Product X",
  product_number: "CHEM-X-001",
  slug: "chemical-product-x",
  completion_score: 85,
  mandatory_completion_score: 100,
  display_completion_score: 85,
  display_mandatory_completion_score: 100,
  cover_image: null,
  status: "published",
  display_status: "published",
  last_modified_full: {
    modified_at: new Date().toISOString(),
    modified_by: "admin",
  },
  card_data: {
    brand_name: {
      display_name: "Brand Name",
      value: "ChemCorp",
    },
    cas_number: {
      display_name: "CAS Number",
      value: "7732-18-5",
    },
    chemical_name: {
      display_name: "Chemical Name",
      value: "Dihydrogen Monoxide",
    },
    product_description: {
      display_name: "Product Description",
      value: "A high-quality chemical compound for industrial applications.",
    },
    product_line: {
      display_name: "Product Line",
      value: "Industrial Solutions",
    },
  },
  show_total_completion_score: true,
  show_mandatory_completion_score: true,
  sds_assets: [
    {
      cdn_url: null,
      file_name: "demo-sds.pdf",
      id: "sds-asset-1",
      name: "Demo SDS",
      number: "SDS-001",
      preview_url: "/preview/sds-1",
      signed_url: "https://example.com/sds-document.pdf",
      size: 195442,
    },
  ],
  tds_assets: [
    {
      cdn_url: null,
      file_name: "demo-tds.pdf",
      id: "tds-asset-1",
      name: "Demo TDS",
      number: "TDS-001",
      preview_url: "/preview/tds-1",
      signed_url: "https://example.com/tds-document.pdf",
      size: 129079,
    },
  ],
  has_multiple_documents: true,
  has_sample_sku: true,
  pim_product_type: "standard",
  ...overrides,
});

const mockProducts = [
  // Standard product with both documents and sample - shows quote/sample buttons
  createMockProduct({
    id: "standard-full",
    name: "Premium Catalyst XR-200",
    product_number: "CAT-XR-200",
    slug: "premium-catalyst-xr-200",
    completion_score: 95,
    mandatory_completion_score: 100,
    display_completion_score: 95,
    display_mandatory_completion_score: 100,
    cover_image:
      "https://images.unsplash.com/photo-1532094349884-543bc11b234d?w=400&h=300&fit=crop",
    pim_product_type: "standard",
    has_sample_sku: true,
    card_data: {
      brand_name: { display_name: "Brand", value: "ChemTech Pro" },
      cas_number: { display_name: "CAS Number", value: "108-88-3" },
      chemical_name: {
        display_name: "Chemical Name",
        value: "Toluene Derivative",
      },
      product_description: {
        display_name: "Description",
        value:
          "High-performance catalyst for industrial polymerization processes. Excellent thermal stability and selectivity.",
      },
      product_line: {
        display_name: "Product Line",
        value: "Advanced Catalysts",
      },
    },
  }),

  // Variant product - shows master badge, no creation buttons
  createMockProduct({
    id: "variant-product",
    name: "Solvent Blue - 50L Drum",
    product_number: "SOL-BLU-50L",
    slug: "solvent-blue-50l",
    completion_score: 78,
    mandatory_completion_score: 85,
    display_completion_score: 78,
    display_mandatory_completion_score: 85,
    cover_image: null,
    pim_product_type: "variant",
    has_sample_sku: false,
    sds_assets: [
      {
        cdn_url: null,
        file_name: "solvent-blue-sds.pdf",
        id: "sds-variant-1",
        name: "Solvent Blue SDS",
        number: "SDS-SOL-001",
        preview_url: "/preview/sds-variant-1",
        signed_url: "https://example.com/solvent-blue-sds.pdf",
        size: 245680,
      },
    ],
    tds_assets: [], // Only SDS, no TDS
    card_data: {
      brand_name: { display_name: "Brand", value: "Industrial Solvents Inc" },
      cas_number: { display_name: "CAS Number", value: "64-17-5" },
      chemical_name: {
        display_name: "Chemical Name",
        value: "Ethanol-based Solvent",
      },
      product_description: {
        display_name: "Description",
        value:
          "Premium grade solvent for cleaning and degreasing applications.",
      },
      product_line: {
        display_name: "Product Line",
        value: "Cleaning Solutions",
      },
    },
  }),

  // Master product - shows master badge, no creation buttons (master products don't show Create buttons)
  createMockProduct({
    id: "master-product",
    name: "Polymer Resin Series",
    product_number: "POL-RES-MASTER",
    slug: "polymer-resin-series",
    completion_score: 100,
    mandatory_completion_score: 100,
    display_completion_score: 100,
    display_mandatory_completion_score: 100,
    cover_image:
      "https://media.istockphoto.com/id/155384204/photo/coloured-polymer-compounds.jpg?s=612x612&w=0&k=20&c=sKeu1murJWfymxhaqZAYxlmXHxen9K4epxPUxRJ3Rh4=",
    pim_product_type: "master",
    has_sample_sku: true,
    tds_assets: [
      {
        cdn_url: null,
        file_name: "polymer-resin-tds.pdf",
        id: "tds-master-1",
        name: "Polymer Resin TDS",
        number: "TDS-POL-001",
        preview_url: "/preview/tds-master-1",
        signed_url: "https://example.com/polymer-resin-tds.pdf",
        size: 189234,
      },
    ],
    sds_assets: [], // Only TDS, no SDS
    card_data: {
      brand_name: { display_name: "Brand", value: "PolyTech Materials" },
      cas_number: { display_name: "CAS Number", value: "9003-53-6" },
      chemical_name: {
        display_name: "Chemical Name",
        value: "Polystyrene Resin",
      },
      product_description: {
        display_name: "Description",
        value:
          "Versatile polymer resin family for injection molding and extrusion applications.",
      },
      product_line: { display_name: "Product Line", value: "Thermoplastics" },
    },
  }),

  // Product with no documents and no samples - minimal buttons
  createMockProduct({
    id: "minimal-product",
    name: "Basic Additive",
    product_number: "ADD-BASIC-001",
    slug: "basic-additive",
    completion_score: 45,
    mandatory_completion_score: 60,
    display_completion_score: 45,
    display_mandatory_completion_score: 60,
    cover_image: null,
    pim_product_type: "standard",
    has_sample_sku: false,
    sds_assets: [],
    tds_assets: [],
    has_multiple_documents: false,
    card_data: {
      brand_name: { display_name: "Brand", value: null }, // No brand name
      cas_number: { display_name: "CAS Number", value: "N/A" },
      chemical_name: {
        display_name: "Chemical Name",
        value: "Proprietary Blend",
      },
      product_description: {
        display_name: "Description",
        value:
          "Basic additive for general applications. Limited documentation available.",
      },
      product_line: {
        display_name: "Product Line",
        value: "Standard Additives",
      },
    },
  }),
];

const ConfigWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  height: calc(100vh - 200px);
  overflow: hidden;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: auto;
    overflow: visible;
  }
`;

const ConfigSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  overflow-y: auto;
  padding-right: ${({ theme }) => theme.resolve("spacing_sm")};

  @media (max-width: 768px) {
    overflow-y: visible;
    padding-right: 0;
  }
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_md")};
  overflow-y: auto;

  @media (max-width: 768px) {
    overflow-y: visible;
  }
`;

const PreviewContainer = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const ResponsivePreviewWrapper = styled.div<{ viewportWidth: string }>`
  width: ${({ viewportWidth }) => viewportWidth};
  max-width: 100%;
  margin: 0 auto;
  transition: width 0.3s ease;
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_light")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  overflow: hidden;
  background-color: #fff;
  padding: 30px;
`;

const ViewportSelector = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
  flex-wrap: wrap;
`;

const ViewportButton = styled.button<{ isActive: boolean }>`
  padding: ${({ theme }) => theme.resolve("spacing_xs")}
    ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ isActive, theme }) =>
    isActive
      ? theme.resolve("color_bg_surface_brand")
      : theme.resolve("color_bg_surface_standard")};
  color: ${({ isActive, theme }) =>
    isActive
      ? theme.resolve("color_text_inverse")
      : theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ isActive, theme }) =>
      isActive
        ? theme.resolve("color_bg_surface_brand")
        : theme.resolve("color_bg_surface_subtle")};
  }
`;

const ConfigGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
  padding: ${({ theme }) => theme.resolve("spacing_md")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
`;

const ConfigGroupTitle = styled.h4`
  margin: 0;
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_semibold")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
`;

const TokenSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }
`;

const ColorPicker = styled.input`
  width: 100%;
  height: 40px;
  padding: ${({ theme }) => theme.resolve("spacing_xxs")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

const NumberInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_sm")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  background: ${({ theme }) => theme.resolve("color_bg_surface_standard")};
  color: ${({ theme }) => theme.resolve("color_text_primary")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.resolve("color_border_brand")};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.resolve("color_brand_3")};
  }

  /* Remove spinner for webkit browsers */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const OpacitySlider = styled.input`
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  appearance: none;
  background: ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_sm")};
  outline: none;
  margin-top: ${({ theme }) => theme.resolve("spacing_xs")};

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_brand")};
    border: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.resolve("color_border_brand")};
    border-radius: 50%;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background: ${({ theme }) => theme.resolve("color_bg_surface_brand")};
    border: ${({ theme }) => theme.resolve("border_2xl")} solid
      ${({ theme }) => theme.resolve("color_border_brand")};
    border-radius: 50%;
    cursor: pointer;
  }
`;

const ColorInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const ColorWithOpacity = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const OpacityValue = styled.span`
  font-size: ${({ theme }) => theme.resolve("font_size_xs")};
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  min-width: 35px;
`;

interface TokenConfiguratorProps {
  onClose: () => void;
  mutateStorefrontMetadata: (
    data: StorefrontMetaData,
    shouldRevalidate: boolean
  ) => void;
}

// Define configurable tokens structure as a function to support dynamic options
const getConfigurableTokens = (colorOptions: Record<string, string>) => ({
  cardProduct: {
    // Background
    background: {
      tokenName: "color_cardProduct_bg",
      label: "Card Background",
      type: "color",
      isCustomColor: true,
      options: colorOptions,
    },
    // Border Colors
    border: {
      tokenName: "color_cardProduct_border",
      label: "Card Border Color",
      type: "select",
      options: colorOptions,
    },
    borderHover: {
      tokenName: "color_cardProduct_border_hover",
      label: "Card Border Hover Color",
      type: "select",
      options: colorOptions,
    },
    // Border Widths - Default State
    borderTop: {
      tokenName: "border_cardProduct_top",
      label: "Border Width - Top",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderRight: {
      tokenName: "border_cardProduct_right",
      label: "Border Width - Right",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderBottom: {
      tokenName: "border_cardProduct_bottom",
      label: "Border Width - Bottom",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderLeft: {
      tokenName: "border_cardProduct_left",
      label: "Border Width - Left",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Border Widths - Hover State
    borderTopHover: {
      tokenName: "border_cardProduct_top_hover",
      label: "Border Width - Top (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderRightHover: {
      tokenName: "border_cardProduct_right_hover",
      label: "Border Width - Right (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderBottomHover: {
      tokenName: "border_cardProduct_bottom_hover",
      label: "Border Width - Bottom (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    borderLeftHover: {
      tokenName: "border_cardProduct_left_hover",
      label: "Border Width - Left (Hover)",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Radii
    borderRadius: {
      tokenName: "radius_cardProduct",
      label: "Card Border Radius",
      type: "select",
      options: radiusTokens,
    },
    imageBorderRadius: {
      tokenName: "radius_cardProduct_image",
      label: "Image Border Radius",
      type: "select",
      options: radiusTokens,
    },
    // Spacing
    spacing: {
      tokenName: "spacing_cardProduct_gap",
      label: "Card Internal Spacing",
      type: "select",
      options: spacingTokens,
    },
    imagePadding: {
      tokenName: "spacing_cardProduct_image_padding",
      label: "Image Padding",
      type: "select",
      options: spacingTokens,
    },
    // Shadow
    shadowX: {
      tokenName: "effect_cardProduct_shadow_x",
      label: "Shadow X Offset",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values
    },
    shadowY: {
      tokenName: "effect_cardProduct_shadow_y",
      label: "Shadow Y Offset",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values
    },
    shadowBlur: {
      tokenName: "effect_cardProduct_shadow_blur",
      label: "Shadow Blur",
      type: "number",
      unit: "px",
      min: 0, // Blur should be non-negative
    },
    shadowSpread: {
      tokenName: "effect_cardProduct_shadow_spread",
      label: "Shadow Spread",
      type: "number",
      unit: "px",
      allowNegative: true, // Allow negative values for inset shadows
    },
    shadowColor: {
      tokenName: "effect_cardProduct_shadow_color",
      label: "Shadow Color",
      type: "colorWithOpacity",
      isCustomColor: true,
    },
    // Text Colors
    textProductName: {
      tokenName: "color_cardProduct_text_product_name",
      label: "Product Name Color",
      type: "select",
      options: colorOptions,
    },
    textAttributeName: {
      tokenName: "color_cardProduct_text_attribute_name",
      label: "Attribute Name Color",
      type: "select",
      options: colorOptions,
    },
    textAttributeValue: {
      tokenName: "color_cardProduct_text_attribute_value",
      label: "Attribute Value Color",
      type: "select",
      options: colorOptions,
    },
    // Icon Color
    iconColor: {
      tokenName: "color_cardProduct_icon",
      label: "Icon Color",
      type: "select",
      options: colorOptions,
    },
    // Aspect Ratio
    aspectRatio: {
      tokenName: "aspect_cardProduct_default",
      label: "Card Aspect Ratio",
      type: "select",
      options: {
        "3 / 2": "3:2 Landscape",
        "1 / 1": "1:1 Square",
        "16 / 9": "16:9 Widescreen",
      },
      description: "Choose the aspect ratio for card products",
    },
  },
  filterPanel: {
    // Backgrounds
    searchBarBackground: {
      tokenName: "color_filterSearchBar_bg",
      label: "Filter Search Bar Background",
      type: "color",
      isCustomColor: true,
    },
    filterGroupBackgroundCollpased: {
      tokenName: "color_filterGroup_bg_collpased",
      label: "Filter Group Background (Collapsed)",
      type: "color",
      isCustomColor: true,
    },
    filterGroupBackgroundExpanded: {
      tokenName: "color_filterGroup_bg_expanded",
      label: "Filter Group Background (Expanded)",
      type: "color",
      isCustomColor: true,
    },
    // Borders
    searchBarBorder: {
      tokenName: "color_filterSearchBar_border",
      label: "Filter Search Bar Border Color",
      type: "select",
      options: colorOptions,
    },
    filterGroupBorderCollpased: {
      tokenName: "color_filterGroup_border_collpased",
      label: "Filter Group Border (Collapsed)",
      type: "select",
      options: colorOptions,
    },
    filterGroupBorderExpanded: {
      tokenName: "color_filterGroup_border_expanded",
      label: "Filter Group Border (Expanded)",
      type: "select",
      options: colorOptions,
    },
    filterPanelBorder: {
      tokenName: "color_filterPanel_border",
      label: "Filter Panel Border Color",
      type: "select",
      options: colorOptions,
    },
    // border width
    filterGroupBorderWidth: {
      tokenName: "border_filterGroup",
      label: "Filter Group Border Width",
      type: "select",
      options: borderPrimitiveTokens,
    },
    filterPanelBorderWidth: {
      tokenName: "border_filterPanel",
      label: "Filter Panel Border Width",
      type: "select",
      options: borderPrimitiveTokens,
    },
    // Spacing
    filterItemGap: {
      tokenName: "spacing_filterItem_gap",
      label: "Filter Item Gap",
      type: "select",
      options: spacingTokens,
    },
    filterPanelSpacing: {
      tokenName: "spacing_filterPanel",
      label: "Filter Panel Spacing",
      type: "select",
      options: spacingTokens,
    },
  },
  productDetail: {
    productBackground: {
      tokenName: "color_bg_surface_product",
      label: "Product Background",
      type: "color",
      isCustomColor: true,
    },
    containerSettings: {
      tokenName: "container_settings",
      label: "Container Settings",
      type: "select",
      options: containerSettingsTokens,
    },
    productDetailLayout: {
      tokenName: "product_detail_layout",
      label: "Product Detail Layout",
      type: "select",
      options: productDetailLayoutTokens,
    },
  },
  buttons: {
    // Shared Button Properties
    borderRadius: {
      tokenName: "radius_button_standard",
      label: "Button Border Radius",
      type: "select",
      options: radiusTokens,
      description: "Border radius for all buttons",
    },
    // Primary Button
    primaryBgEnabled: {
      tokenName: "color_button_bg_primary_enabled",
      label: "Primary Background (Default)",
      type: "select",
      options: colorOptions,
    },
    primaryBgHover: {
      tokenName: "color_button_bg_primary_hover",
      label: "Primary Background (Hover)",
      type: "select",
      options: colorOptions,
    },
    primaryTextEnabled: {
      tokenName: "color_button_text_primary_enabled",
      label: "Primary Text Color",
      type: "select",
      options: colorOptions,
    },
    primaryTextHover: {
      tokenName: "color_button_text_primary_hover",
      label: "Primary Text Color (Hover)",
      type: "select",
      options: colorOptions,
    },
    primaryBorderEnabled: {
      tokenName: "color_button_border_primary_enabled",
      label: "Primary Border Color",
      type: "select",
      options: colorOptions,
    },
    primaryBorderHover: {
      tokenName: "color_button_border_primary_hover",
      label: "Primary Border Color (Hover)",
      type: "select",
      options: colorOptions,
    },
    primaryIconEnabled: {
      tokenName: "color_button_icon_primary_enabled",
      label: "Primary Icon Color",
      type: "select",
      options: colorOptions,
    },
    primaryIconHover: {
      tokenName: "color_button_icon_primary_hover",
      label: "Primary Icon Color (Hover)",
      type: "select",
      options: colorOptions,
    },
  },
  customColors: {
    custom1: {
      tokenName: "color_custom_1",
      label: "Custom Color 1",
      type: "color",
      isCustomColor: true,
    },
    custom2: {
      tokenName: "color_custom_2",
      label: "Custom Color 2",
      type: "color",
      isCustomColor: true,
    },
    custom3: {
      tokenName: "color_custom_3",
      label: "Custom Color 3",
      type: "color",
      isCustomColor: true,
    },
    custom4: {
      tokenName: "color_custom_4",
      label: "Custom Color 4",
      type: "color",
      isCustomColor: true,
    },
  },
  customBorders: {
    custom1: {
      tokenName: "border_custom_1",
      label: "Custom Border 1",
      type: "number",
      unit: "px",
      defaultValue: "6",
      min: 0, // Border widths should be non-negative
    },
    custom2: {
      tokenName: "border_custom_2",
      label: "Custom Border 2",
      type: "number",
      unit: "px",
      defaultValue: "8",
      min: 0, // Border widths should be non-negative
    },
  },
});

// Mock context providers for preview
const MockAuthProvider = ({
  children,
  userRole,
}: {
  children: React.ReactNode;
  userRole: "guest" | "buyer" | "seller";
}) => {
  const getMockAuthValue = (role: "guest" | "buyer" | "seller") => {
    const baseAuthValue = {
      user: null,
      logout: async () => {},
      completeLogout: () => {},
      initiateLogin: async () => ({ user: null, loginError: null }),
      updateUser: () => {},
      loginError: undefined,
      logoutError: undefined,
      isLoadingSession: false,
      updatePermissions: () => {},
      permissions: [],
      hasPermission: (p?: any, permission_type?: "some" | "every") =>
        role === "seller" &&
        (p === "modify_products" ||
          (Array.isArray(p) && p.includes("modify_products"))),
      roleIsSellerAdmin: false,
      roleIsAgilisAdmin: false,
      roleIsBuyerAdmin: false,
      roleIsDistributorAdmin: false,
      roleIsDistributor: false,
      roleIsAdmin: false,
    };

    switch (role) {
      case "guest":
        return {
          ...baseAuthValue,
          role: "guest" as const,
          roleIsSomeKindOfSeller: false,
          roleIsSomeKindOfBuyer: false,
          roleIsBuyer: false,
          roleIsSellerStandard: false,
          roleIsGuest: true,
        };
      case "buyer":
        return {
          ...baseAuthValue,
          role: "buyer_standard" as const,
          roleIsSomeKindOfSeller: false,
          roleIsSomeKindOfBuyer: true,
          roleIsBuyer: true,
          roleIsSellerStandard: false,
          roleIsGuest: false,
        };
      case "seller":
        return {
          ...baseAuthValue,
          role: "seller_standard" as const,
          roleIsSomeKindOfSeller: true,
          roleIsSomeKindOfBuyer: false,
          roleIsBuyer: false,
          roleIsSellerStandard: true,
          roleIsGuest: false,
        };
      default:
        return {
          ...baseAuthValue,
          role: "guest" as const,
          roleIsSomeKindOfSeller: false,
          roleIsSomeKindOfBuyer: false,
          roleIsBuyer: false,
          roleIsSellerStandard: false,
          roleIsGuest: true,
        };
    }
  };

  const mockAuthValue = getMockAuthValue(userRole);
  return <Auth.Provider value={mockAuthValue}>{children}</Auth.Provider>;
};

// Mock Store Provider that ensures quote/sample buttons are enabled
const MockStoreProvider = ({ children }: { children: React.ReactNode }) => {
  const { storeState } = useContext(Store);

  // Create a mock store state with enabled quote/sample requests
  const mockStoreState = {
    ...storeState,
    storefront_metadata: {
      ...storeState.storefront_metadata,
      quote_requests_visibility: "enabled" as const,
      sample_requests_visibility: "enabled" as const,
    },
  };

  const mockStoreValue = {
    storeState: mockStoreState,
    storeDispatch: () => {}, // No-op for preview
  };

  return <Store.Provider value={mockStoreValue}>{children}</Store.Provider>;
};

// Wrapper that provides both mock auth and store contexts
const PreviewWrapper = ({
  children,
  userRole,
}: {
  children: React.ReactNode;
  userRole: "guest" | "buyer" | "seller";
}) => {
  return (
    <MockStoreProvider>
      <MockAuthProvider userRole={userRole}>{children}</MockAuthProvider>
    </MockStoreProvider>
  );
};

export const AdvancedThemeEditor = ({
  onClose,
  mutateStorefrontMetadata,
}: TokenConfiguratorProps) => {
  const { notifySuccess, notifyError } = useContext(Notifications);
  const { storeState, storeDispatch } = useContext(Store);
  const { t } = useTranslation();
  const { storefront_id, storefront_metadata } = storeState;
  const branding = storefront_metadata.theme_object.branding;

  const [submitting, setSubmitting] = useState(false);
  const [selectedViewport, setSelectedViewport] = useState("desktop");
  const [previewUserRole, setPreviewUserRole] =
    useState<"guest" | "buyer" | "seller">("guest");

  // Initialize form state from saved designTokens
  const initializeFormState = () => {
    const initialTokenOverrides: Record<string, string> = {};
    const initialCustomValues: Record<string, any> = {};

    // Check if we have saved designTokens in the new schema
    const savedThemeObject =
      storefront_metadata.theme_object as ThemeObjectSchema;
    if (savedThemeObject?.designTokens) {
      const { designTokens } = savedThemeObject;

      // Load CardProduct, filterPanel and Button token overrides
      [
        designTokens.cardProduct,
        designTokens.buttons,
        designTokens.filterPanel,
        designTokens.productDetail,
      ].forEach((category) => {
        if (category) {
          Object.entries(category).forEach(([tokenName, tokenValue]) => {
            if (typeof tokenValue === "object" && tokenValue.type === "token") {
              initialTokenOverrides[tokenName] = tokenValue.value;
            } else if (
              typeof tokenValue === "object" &&
              tokenValue.type === "custom"
            ) {
              initialCustomValues[tokenName] = tokenValue.value;

              // Parse rgba values for color with opacity
              if (
                tokenName === "effect_cardProduct_shadow_color" &&
                typeof tokenValue.value === "string" &&
                tokenValue.value.startsWith("rgba(")
              ) {
                const match = tokenValue.value.match(
                  /rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/
                );
                if (match) {
                  const [, r, g, b, a] = match;
                  const hex =
                    "#" +
                    (
                      (1 << 24) +
                      (parseInt(r) << 16) +
                      (parseInt(g) << 8) +
                      parseInt(b)
                    )
                      .toString(16)
                      .slice(1);
                  initialCustomValues[`${tokenName}_color`] = hex;
                  initialCustomValues[`${tokenName}_opacity`] = parseFloat(a);
                }
              }
            }
          });
        }
      });

      // Load custom colors
      if (designTokens.customColors) {
        Object.entries(designTokens.customColors).forEach(
          ([tokenName, value]) => {
            initialCustomValues[tokenName] = value;
          }
        );
      }

      // Load custom borders (remove px suffix for form input)
      if (designTokens.customBorders) {
        Object.entries(designTokens.customBorders).forEach(
          ([tokenName, value]) => {
            const numericValue =
              typeof value === "string" ? value.replace("px", "") : value;
            initialCustomValues[tokenName] = numericValue;
          }
        );
      }
    }

    return { initialTokenOverrides, initialCustomValues };
  };

  const { initialTokenOverrides, initialCustomValues } = initializeFormState();
  const [tokenOverrides, setTokenOverrides] = useState<Record<string, string>>(
    initialTokenOverrides
  );
  const [customValues, setCustomValues] =
    useState<Record<string, any>>(initialCustomValues);

  // Build color options including custom colors from current form state
  const getColorOptions = (): typeof colorPrimitiveTokens &
    Record<string, string> => {
    const baseOptions = { ...colorPrimitiveTokens };

    // Add custom colors from current form state
    Object.entries(customValues).forEach(([tokenName, value]) => {
      if (tokenName.startsWith("color_custom_") && value) {
        (baseOptions as Record<string, string>)[
          tokenName
        ] = `Custom (${value})`;
      }
    });

    return baseOptions;
  };

  const configurableTokens = getConfigurableTokens(getColorOptions());

  const viewportSizes = {
    mobile: "375px",
    desktop: "100%",
  };

  // Create preview theme with current token overrides
  const previewTheme = useMemo(() => {
    const baseTheme = makeNewTheme(branding, storeState.tenant_id);

    if (
      Object.keys(tokenOverrides).length === 0 &&
      Object.keys(customValues).length === 0
    ) {
      return baseTheme;
    }

    // Create a new theme with direct token value overrides
    const themeWithOverrides = {
      ...baseTheme,
      tokens: {
        ...baseTheme.tokens,
      },
      customValues, // Add custom values to theme
    };

    // Override primitive tokens with custom values first
    if (customValues) {
      Object.entries(customValues).forEach(([tokenName, customValue]) => {
        if (
          customValue !== undefined &&
          customValue !== null &&
          customValue !== ""
        ) {
          // Override primitive tokens directly
          if (
            themeWithOverrides.tokens.primitiveTokens.colors[
              tokenName as keyof typeof themeWithOverrides.tokens.primitiveTokens.colors
            ]
          ) {
            themeWithOverrides.tokens.primitiveTokens.colors[
              tokenName as keyof typeof themeWithOverrides.tokens.primitiveTokens.colors
            ] = customValue;
          }
        }
      });
    }

    // Override the resolve function to use our token overrides
    const originalResolve = themeWithOverrides.resolve;
    themeWithOverrides.resolve = (tokenName: AllTokenKeys) => {
      const originalValue = originalResolve(tokenName);

      // Check for custom values first (this includes custom colors like color_cardProduct_bg)
      if (
        customValues[tokenName] !== undefined &&
        customValues[tokenName] !== null &&
        customValues[tokenName] !== ""
      ) {
        const customValue = customValues[tokenName];

        // For numbers with units, append the unit
        if (
          typeof customValue === "number" ||
          (typeof customValue === "string" && /^\d+$/.test(customValue))
        ) {
          // Check if this is a shadow or border token that needs px
          if (
            tokenName.includes("shadow") ||
            tokenName.includes("border_custom")
          ) {
            return `${customValue}px`;
          }
        }

        // For color values, return as-is (hex codes, etc.)
        return customValue;
      }

      // If this token has an override, use the primitive token value directly
      if (tokenOverrides[tokenName]) {
        const primitiveTokenName = tokenOverrides[tokenName];

        // For aspect ratio default selection, primitiveTokenName is the CSS ratio value
        if (tokenName === "aspect_cardProduct_default") {
          return primitiveTokenName;
        }

        // Get the primitive token value - need to access it properly
        const allPrimitiveTokens = {
          ...themeWithOverrides.tokens.primitiveTokens.colors,
          ...themeWithOverrides.tokens.primitiveTokens.spacing,
          ...themeWithOverrides.tokens.primitiveTokens.radii,
          ...themeWithOverrides.tokens.primitiveTokens.fontSizes,
          ...themeWithOverrides.tokens.primitiveTokens.fontWeights,
          ...themeWithOverrides.tokens.primitiveTokens.borders,
        };

        const primitiveValue =
          allPrimitiveTokens[
            primitiveTokenName as keyof typeof allPrimitiveTokens
          ];

        if (primitiveValue) {
          return primitiveValue;
        }
      }

      return originalValue;
    };

    return themeWithOverrides;
  }, [branding, tokenOverrides, customValues, storeState.tenant_id]);

  const { handleSubmit } = useFormWrapper({
    defaultValues: {},
  });

  const handleTokenChange = (tokenName: string, primitiveTokenName: string) => {
    setTokenOverrides((prev) => ({
      ...prev,
      [tokenName]: primitiveTokenName,
    }));
  };

  const handleCustomValueChange = (tokenName: string, value: any) => {
    setCustomValues((prev) => ({
      ...prev,
      [tokenName]: value,
    }));
  };

  const onSubmit = async () => {
    setSubmitting(true);
    try {
      // Build designTokens structure from form state
      const designTokens: DesignTokensSchema = {};

      // Process token overrides
      Object.entries(tokenOverrides).forEach(([tokenName, primitiveToken]) => {
        // Find which category this token belongs to
        const category = Object.entries(configurableTokens).find(
          ([_, config]) =>
            Object.values(config).some(
              (item: any) => item.tokenName === tokenName
            )
        )?.[0];

        if (category === "cardProduct") {
          if (!designTokens.cardProduct) designTokens.cardProduct = {};
          (designTokens.cardProduct as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "filterPanel") {
          if (!designTokens.filterPanel) designTokens.filterPanel = {};
          (designTokens.filterPanel as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "productDetail") {
          if (!designTokens.productDetail) designTokens.productDetail = {};
          (designTokens.productDetail as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        } else if (category === "buttons") {
          if (!designTokens.buttons) designTokens.buttons = {};
          (designTokens.buttons as any)[tokenName] = {
            type: "token",
            value: primitiveToken,
          };
        }
      });

      // Process custom values
      Object.entries(customValues).forEach(([tokenName, value]) => {
        // Custom colors
        if (tokenName.startsWith("color_custom_")) {
          if (!designTokens.customColors) designTokens.customColors = {};
          designTokens.customColors[tokenName] = value;
        }
        // Custom borders
        else if (tokenName.startsWith("border_custom_")) {
          if (!designTokens.customBorders) designTokens.customBorders = {};
          designTokens.customBorders[tokenName] = `${value}px`;
        }
        // Other custom values (like shadow offsets)
        else {
          const category = Object.entries(configurableTokens).find(
            ([_, config]) =>
              Object.values(config).some(
                (item: any) => item.tokenName === tokenName
              )
          )?.[0];

          if (category === "cardProduct") {
            if (!designTokens.cardProduct) designTokens.cardProduct = {};
            (designTokens.cardProduct as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          } else if (category === "filterPanel") {
            if (!designTokens.filterPanel) designTokens.filterPanel = {};
            (designTokens.filterPanel as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          } else if (category === "productDetail") {
            if (!designTokens.productDetail) designTokens.productDetail = {};
            (designTokens.productDetail as any)[tokenName] = {
              type: "custom",
              value: value,
            };
          }
        }
      });

      // Create new ThemeObjectSchema structure
      const themeObject: ThemeObjectSchema = {
        version: "1.0",
        branding: {
          ...(branding.brandColor && { brandColor: branding.brandColor }),
          ...(branding.portalLogoUrl && {
            portalLogoUrl: branding.portalLogoUrl,
          }),
          ...(branding.publicLogoUrl && {
            publicLogoUrl: branding.publicLogoUrl,
          }),
          ...(branding.customFontFamily && {
            customFontFamily: branding.customFontFamily,
          }),
          ...(branding.favIconUrl && { favIconUrl: branding.favIconUrl }),
        },
        // Only include designTokens if there are any overrides
        ...(Object.keys(designTokens).length > 0 ? { designTokens } : {}),
      };

      const patchBody: Pick<StorefrontMetaData, "theme_object"> = {
        theme_object: themeObject as any,
      };

      const { data: returnedMetadata } = await Axios.patch<StorefrontMetaData>(
        endpoints.v1_storefronts_id_metadata(storefront_id),
        patchBody
      );

      storeDispatch({
        type: "SET_STOREFRONT_METADATA",
        payload: returnedMetadata,
      });

      storeDispatch({
        type: "SET_THEME",
        payload: makeNewTheme(themeObject, storeState.tenant_id),
      });

      notifySuccess(t("Theme changes saved"));
      setSubmitting(false);
      mutateStorefrontMetadata(returnedMetadata, false);
      onClose();
    } catch (error) {
      notifyError(t("There was an error saving the theme changes"), { error });
      setSubmitting(false);
    }
  };

  const renderTokenConfigurator = (
    sectionKey: string,
    config: Record<string, any>
  ) => {
    const getSectionTitle = (key: string) => {
      switch (key) {
        case "cardProduct":
          return "Product Cards";
        case "filterPanel":
          return "Filter Panel";
        case "productDetail":
          return "Product Detail";
        case "buttons":
          return "Buttons";
        case "customColors":
          return "Custom Colors";
        case "customBorders":
          return "Custom Borders";
        default:
          return key;
      }
    };

    return (
      <ConfigGroup key={sectionKey}>
        <ConfigGroupTitle>{getSectionTitle(sectionKey)}</ConfigGroupTitle>
        {Object.entries(config).map(([configKey, tokenConfig]) => (
          <div key={configKey}>
            <SoftHeader2>{tokenConfig.label}</SoftHeader2>
            {tokenConfig.description && (
              <p
                style={{
                  fontSize: fontSizeTokens.font_size_sm,
                  color: "#6F777F",
                  margin: `${spacingTokens.spacing_xs} 0`,
                }}
              >
                {tokenConfig.description}
              </p>
            )}

            {/* Color picker for custom colors */}
            {tokenConfig.type === "color" && tokenConfig.isCustomColor && (
              <ColorPicker
                type="color"
                value={
                  customValues[tokenConfig.tokenName] !== undefined &&
                  customValues[tokenConfig.tokenName] !== null &&
                  customValues[tokenConfig.tokenName] !== ""
                    ? customValues[tokenConfig.tokenName]
                    : previewTheme.resolve(
                        tokenConfig.tokenName as AllTokenKeys
                      )
                }
                onChange={(e) => {
                  handleCustomValueChange(
                    tokenConfig.tokenName,
                    e.target.value
                  );
                }}
              />
            )}

            {/* Color picker with opacity for shadow colors */}
            {tokenConfig.type === "colorWithOpacity" &&
              tokenConfig.isCustomColor && (
                <ColorInputWrapper>
                  <ColorWithOpacity>
                    <ColorPicker
                      type="color"
                      value={
                        customValues[`${tokenConfig.tokenName}_color`] ||
                        "#000000"
                      }
                      onChange={(e) => {
                        const opacity =
                          customValues[`${tokenConfig.tokenName}_opacity`] ||
                          0.1;
                        handleCustomValueChange(
                          `${tokenConfig.tokenName}_color`,
                          e.target.value
                        );
                        // Update the combined value with opacity
                        const rgb = parseInt(e.target.value.slice(1), 16);
                        const r = (rgb >> 16) & 255;
                        const g = (rgb >> 8) & 255;
                        const b = rgb & 255;
                        handleCustomValueChange(
                          tokenConfig.tokenName,
                          `rgba(${r}, ${g}, ${b}, ${opacity})`
                        );
                      }}
                    />
                    <OpacityValue>
                      {Math.round(
                        (customValues[`${tokenConfig.tokenName}_opacity`] ||
                          0.1) * 100
                      )}
                      %
                    </OpacityValue>
                  </ColorWithOpacity>
                  <OpacitySlider
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={
                      customValues[`${tokenConfig.tokenName}_opacity`] || 0.1
                    }
                    onChange={(e) => {
                      const opacity = parseFloat(e.target.value);
                      handleCustomValueChange(
                        `${tokenConfig.tokenName}_opacity`,
                        opacity
                      );
                      // Update the combined value with new opacity
                      const color =
                        customValues[`${tokenConfig.tokenName}_color`] ||
                        "#000000";
                      const rgb = parseInt(color.slice(1), 16);
                      const r = (rgb >> 16) & 255;
                      const g = (rgb >> 8) & 255;
                      const b = rgb & 255;
                      handleCustomValueChange(
                        tokenConfig.tokenName,
                        `rgba(${r}, ${g}, ${b}, ${opacity})`
                      );
                    }}
                  />
                </ColorInputWrapper>
              )}

            {/* Number input for custom values */}
            {tokenConfig.type === "number" && (
              <NumberInput
                type="number"
                min={tokenConfig.allowNegative ? undefined : tokenConfig.min}
                value={
                  customValues[tokenConfig.tokenName] ||
                  tokenConfig.defaultValue ||
                  "0"
                }
                onChange={(e) => {
                  handleCustomValueChange(
                    tokenConfig.tokenName,
                    e.target.value
                  );
                }}
                placeholder={`Enter value${
                  tokenConfig.unit ? ` in ${tokenConfig.unit}` : ""
                }`}
              />
            )}

            {/* Boolean checkbox */}
            {tokenConfig.type === "boolean" && (
              <CheckboxWrapper>
                <Checkbox
                  type="checkbox"
                  checked={customValues[tokenConfig.tokenName] || false}
                  onChange={(e) => {
                    handleCustomValueChange(
                      tokenConfig.tokenName,
                      e.target.checked
                    );
                  }}
                />
                <span>{tokenConfig.label}</span>
              </CheckboxWrapper>
            )}

            {/* Select dropdown for token options */}
            {tokenConfig.type === "select" && tokenConfig.options && (
              <TokenSelect
                value={tokenOverrides[tokenConfig.tokenName] || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) {
                    handleTokenChange(tokenConfig.tokenName, value);
                  } else {
                    setTokenOverrides((prev) => {
                      const newOverrides = { ...prev };
                      delete newOverrides[tokenConfig.tokenName];
                      return newOverrides;
                    });
                  }
                }}
              >
                <option value="">{t("Use default value")}</option>
                {Object.entries(tokenConfig.options).map(([key, value]) => (
                  <option key={key} value={key}>
                    {key} ({value})
                  </option>
                ))}
              </TokenSelect>
            )}
          </div>
        ))}
      </ConfigGroup>
    );
  };

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <ConfigWrapper>
        <ConfigSection>
          <H3>{t("Token Configuration")}</H3>
          {Object.entries(configurableTokens).map(([sectionKey, config]) =>
            renderTokenConfigurator(sectionKey, config)
          )}
        </ConfigSection>

        <PreviewSection>
          <H3>{t("Live Preview")}</H3>
          <PreviewContainer>
            <SoftHeader2>{t("Product Card Preview")}</SoftHeader2>

            <ViewportSelector>
              {Object.entries(viewportSizes).map(([viewport, width]) => (
                <ViewportButton
                  key={viewport}
                  isActive={selectedViewport === viewport}
                  onClick={() => setSelectedViewport(viewport)}
                  type="button"
                >
                  {viewport.charAt(0).toUpperCase() + viewport.slice(1)} (
                  {width})
                </ViewportButton>
              ))}
            </ViewportSelector>

            <div style={{ marginBottom: spacingTokens.spacing_xl }}>
              <SoftHeader2 style={{ marginBottom: spacingTokens.spacing_md }}>
                {t("User Role Preview")}
              </SoftHeader2>
              <ViewportSelector>
                {(["guest", "buyer", "seller"] as const).map((role) => (
                  <ViewportButton
                    key={role}
                    isActive={previewUserRole === role}
                    onClick={() => setPreviewUserRole(role)}
                    type="button"
                  >
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </ViewportButton>
                ))}
              </ViewportSelector>
              <p
                style={{
                  fontSize: fontSizeTokens.font_size_sm,
                  color: "#6F777F",
                  margin: `${spacingTokens.spacing_xs} 0 0 0`,
                }}
              >
                {previewUserRole === "guest" &&
                  "Guest users see 'Get Quote' and 'Request Sample' buttons (no admin metadata)"}
                {previewUserRole === "buyer" &&
                  "Buyer users see 'Get Quote' and 'Request Sample' buttons (no admin metadata)"}
                {previewUserRole === "seller" &&
                  "Seller users see 'Create Quote' and 'Create Sample' buttons, plus admin metadata (completion scores, status, calendar icons)"}
              </p>
            </div>

            <ResponsivePreviewWrapper
              viewportWidth={
                viewportSizes[selectedViewport as keyof typeof viewportSizes]
              }
            >
              <PreviewWrapper userRole={previewUserRole}>
                <ThemeProvider theme={previewTheme}>
                  <ProductListMainWrapper
                    data-testid="product-list-container"
                    style={{
                      height: "100%",
                      padding: `0 ${spacingTokens.spacing_3xl} ${spacingTokens.spacing_3xl} 0`,
                    }}
                    gridView={true}
                  >
                    {mockProducts.map((product) => (
                      <CardProduct
                        key={product.id}
                        product={product as any}
                        onProductClick={() => {}}
                        onQuoteClick={() => {}}
                        onSampleClick={() => {}}
                        onDocumentClick={() => {}}
                      />
                    ))}
                  </ProductListMainWrapper>
                </ThemeProvider>
              </PreviewWrapper>
            </ResponsivePreviewWrapper>
          </PreviewContainer>

          <PreviewContainer>
            <SoftHeader2>{t("Button Preview")}</SoftHeader2>
            <ThemeProvider theme={previewTheme}>
              <ButtonTestPage />
            </ThemeProvider>
          </PreviewContainer>
        </PreviewSection>
      </ConfigWrapper>

      <TwoColumnLayout>
        <div style={{ display: "flex", gap: "1rem", marginTop: "2rem" }}>
          <PrimaryButtonMedium loading={submitting}>
            {t("Save Theme Changes")}
          </PrimaryButtonMedium>
          <CancelButtonSmall onClick={onClose}>{t("Cancel")}</CancelButtonSmall>
        </div>
      </TwoColumnLayout>
    </Form>
  );
};
